// packages/charts/src/table/calendar.ts
//
// The eight calendar operations `alignComparisonByDate` used to take from
// `date-fns`, on plain `Date` in LOCAL time with the same semantics for the
// date-only inputs the table model feeds them (plan 2026-09-13
// one-table-model, B1b). Written out rather than depended on: `date-fns` is
// declared only in the frontend app, the report sandbox and the e-mail
// runtime do not carry it, and this package's pure entry has no runtime
// dependency to grow one for a week-start computation. `date-fns` is on the
// zone-1 forbidden list in `eslint.config.mjs` so it cannot arrive later.
//
// Local time, like date-fns: a "2026-09-01" is local midnight, and every
// operation reads and writes local calendar fields, so a shift across a DST
// change lands on the calendar day the reader expects rather than 23 hours
// later.

/** Local midnight for an ISO calendar date; an invalid string yields an
 *  invalid Date (`Number.isNaN(d.getTime())`), as `parseISO` did. */
export function parseISODate(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return new Date(Number.NaN);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // `new Date(2026, 1, 31)` rolls into March; date-fns' parseISO refuses it.
  if (d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) return new Date(Number.NaN);
  return d;
}

export function addDays(date: Date, amount: number): Date {
  const out = new Date(date.getTime());
  out.setDate(out.getDate() + amount);
  return out;
}

/** Whole calendar days from `right` to `left`, time of day ignored. */
export function differenceInCalendarDays(left: Date, right: Date): number {
  const l = Date.UTC(left.getFullYear(), left.getMonth(), left.getDate());
  const r = Date.UTC(right.getFullYear(), right.getMonth(), right.getDate());
  return Math.round((l - r) / 86_400_000);
}

/** The Monday of the date's ISO week, at local midnight. */
export function startOfISOWeek(date: Date): Date {
  const out = new Date(date.getTime());
  const back = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - back);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function startOfQuarter(date: Date): Date {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

export function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

/** A year earlier, clamped to the month's last day (29 Feb → 28 Feb), as
 *  `subYears` does. */
export function subYears(date: Date, amount: number): Date {
  const year = date.getFullYear() - amount;
  const month = date.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const out = new Date(date.getTime());
  out.setFullYear(year, month, Math.min(date.getDate(), lastDay));
  return out;
}

/** `amount` months earlier (negative = later), clamped to the target month's
 *  last day (31 Mar → 28/29 Feb), as date-fns `subMonths` does. */
export function subMonths(date: Date, amount: number): Date {
  const total = date.getFullYear() * 12 + date.getMonth() - amount;
  const year = Math.floor(total / 12);
  const month = total - year * 12;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const out = new Date(date.getTime());
  out.setFullYear(year, month, Math.min(date.getDate(), lastDay));
  return out;
}
